import type { VersionedDraftKit } from '@/lib/versioned-draft';
import { canSaveVersion } from '@/lib/versioned-draft';
import type {
  CriterionContent,
  CriterionDetail,
  CriterionDraftContent,
  CriterionScoringDraft,
} from '@matter_extractor/shared';
import {
  CRITERION_EXAMPLES_MAX,
  CRITERION_SCORING_BANDS_MAX,
  CRITERION_SCORING_BANDS_MIN,
  CRITERION_SCORING_TOP,
  bandLowerBounds,
  criterionScoringSchema,
} from '@matter_extractor/shared';

/**
 * What the Critères editor holds while somebody types (refonte modulaire §6).
 *
 * The comparison that greys « Enregistrer », the version numbering and the
 * three refusals are in `versioned-draft.ts` and hold for every versioned
 * object. What is here is the SHAPE of a criterion's draft and the two
 * functions that cross between it and the wire - which is exactly what
 * `VersionedDraftKit` asks for, no more.
 *
 * ## Why the draft is not a `CriterionContent`
 *
 * A `CriterionContent` is a version: its name and direction are non-empty and
 * its examples are trimmed. A DRAFT is a half-written thought - a blank example
 * field that was just added, a direction with a trailing space, a name nobody
 * has typed yet. Keeping the two shapes apart is what lets the editor render
 * exactly what was typed while `normalizedContent` decides, once, what would
 * be sent.
 */

export interface CriterionDraft {
  readonly name: string;
  readonly direction: string;
  /** One field per example, IN ORDER, blanks included while they are being typed. */
  readonly examples: readonly string[];
  /**
   * The scoring being typed, or `null` when the criterion has none (§6).
   *
   * The DRAFT shape and not `CriterionScoring`: a band added by « Ajouter une
   * tranche » has no upper bound yet, which is a legal thing to be holding and
   * an illegal thing to save.
   */
  readonly scoring: CriterionScoringDraft | null;
}

export const EMPTY_CRITERION_DRAFT: CriterionDraft = {
  name: '',
  direction: '',
  examples: [],
  scoring: null,
};

/**
 * A draft from a stored content - a version's, or the server draft's.
 *
 * The parameter is `CriterionDraftContent` - the shared name for a partial
 * content - and not a locally derived `Partial<CriterionContent>`, because the
 * two are NOT the same type under `exactOptionalPropertyTypes`: the local one
 * promises that a present key holds a `string`, while a draft off the wire
 * carries `string | undefined`. One vocabulary on both sides of the round trip
 * is what keeps them from drifting.
 *
 * A draft may legitimately carry no name at all: a direction typed halfway has
 * to survive a reload, and a `CriterionContent` cannot express that.
 */
export function draftFromContent(content: CriterionDraftContent): CriterionDraft {
  return {
    name: content.name ?? '',
    direction: content.direction ?? '',
    examples: [...(content.examples ?? [])],
    // A version's scoring is a draft's scoring with every upper bound filled,
    // so the two cross without a conversion - which is why the draft shape is
    // the loose one and not a third vocabulary.
    scoring: content.scoring ?? null,
  };
}

/**
 * What the editor opens on: the server draft when there is one, the
 * `développement` version otherwise, and an empty editor for a criterion that
 * does not exist yet (§4, §6).
 *
 * The order is the pattern's: a draft « protects from a reload », so it wins
 * over the version it was started from. For a NEW criterion there is neither
 * entity nor server draft - the saisie lives only in the page until the first
 * save writes both at once.
 */
export function openingDraft(detail: CriterionDetail | null): CriterionDraft {
  if (detail === null) return EMPTY_CRITERION_DRAFT;
  return draftFromContent(detail.draft?.content ?? detail.developmentContent);
}

/**
 * The content a draft would become, or `null` when it could not become one.
 *
 * Trimmed then emptied-out, in that order, which is the order
 * `criterionExampleSchema` states: a field holding three spaces is an example
 * an operator believes they wrote, and it would reach the scribe as a blank
 * line. `null` means the two required fields are not both filled, or the
 * scoring is not a scale yet - which is the whole of what makes the button
 * inactive (§6).
 *
 * The scoring ASKS THE SCHEMA rather than re-deciding, the way the experience's
 * own `normalizedContent` does: `criterionScoringSchema` is what the server
 * will parse, so a conversion written by hand here would be a second
 * implementation of the three refinements - and the one that drifts. It is also
 * what narrows `upper: number | null` to the `number` a version's band holds,
 * without a non-null assertion.
 */
export function normalizedContent(draft: CriterionDraft): CriterionContent | null {
  const name = draft.name.trim();
  const direction = draft.direction.trim();
  if (name === '' || direction === '') return null;

  let scoring: CriterionContent['scoring'] = null;
  if (draft.scoring !== null) {
    const parsed = criterionScoringSchema.safeParse(draft.scoring);
    if (!parsed.success) return null;
    scoring = parsed.data;
  }

  return {
    name,
    direction,
    examples: draft.examples.map((example) => example.trim()).filter((example) => example !== ''),
    scoring,
  };
}

/**
 * What an autosave SENDS - and the one thing about it that is not obvious.
 *
 * `criterionSaveDraftSchema` parses `criterionContentSchema.partial()`, and
 * `.partial()` makes each field OPTIONAL without loosening it: `name` is still
 * `z.string().trim().min(1)` when it is there. So an empty name has to be
 * OMITTED and cannot be sent as `''` - a draft of a criterion nobody has named
 * yet would otherwise be answered with a 400, which is the one refusal an
 * autosave must never produce: it fires while somebody types, and a red box
 * appearing on the third keystroke of an empty field would teach an operator
 * that the editor is broken.
 *
 * Blank examples go the same way, for the reason `criterionExampleSchema`
 * gives: a field holding three spaces is not an example. `examples` is sent
 * even when empty, because an emptied list IS a change the draft must record -
 * omitting it would make « j'ai retiré mes trois exemples » unsaveable.
 */
export function draftPayload(draft: CriterionDraft): CriterionDraftContent {
  const payload: CriterionDraftContent = {
    examples: draft.examples.map((example) => example.trim()).filter((example) => example !== ''),
    // Sent even when null, for the reason `examples` is sent when empty: « j'ai
    // retiré la notation » is a change, and an omitted key would make it the
    // one edit of this screen a reload undoes.
    scoring: draft.scoring,
  };
  const name = draft.name.trim();
  if (name !== '') payload.name = name;
  const direction = draft.direction.trim();
  if (direction !== '') payload.direction = direction;
  return payload;
}

/** Everything the versioned editor needs to run a criterion's draft (§4). */
export const CRITERION_DRAFT_KIT: VersionedDraftKit<
  CriterionContent,
  CriterionDraftContent,
  CriterionDraft
> = {
  opening: openingDraft,
  fromContent: draftFromContent,
  normalized: normalizedContent,
  payload: draftPayload,
};

/** Is « Enregistrer une nouvelle version » usable? The three refusals of §4. */
export function canSaveCriterion(draft: CriterionDraft, detail: CriterionDetail | null): boolean {
  return canSaveVersion(draft, detail, CRITERION_DRAFT_KIT);
}

/** Can another example field be added? H10 caps the list at thirty (§6). */
export function canAddExample(draft: CriterionDraft): boolean {
  return draft.examples.length < CRITERION_EXAMPLES_MAX;
}

/* --------------------------------------------------------- the scale, edited */

/** Can another band be added? H44 caps the scale at ten (§6). */
export function canAddBand(scoring: CriterionScoringDraft): boolean {
  return scoring.bands.length < CRITERION_SCORING_BANDS_MAX;
}

/**
 * Can this band be removed? H44 floors the scale at two (§6).
 *
 * The floor is enforced on the GESTURE and not only on the save, because
 * `criterionScoringDraftSchema` carries the same minimum: a scale cut down to
 * one band would be an autosave answered 400, which is the one refusal a
 * screen that fires while somebody types must never produce.
 */
export function canRemoveBand(scoring: CriterionScoringDraft): boolean {
  return scoring.bands.length > CRITERION_SCORING_BANDS_MIN;
}

/**
 * « Ajouter une tranche » inserts BEFORE the last one (§6, maquette
 * `Main.dc.html`), with no upper bound and no text.
 *
 * Before and not after, and that is the whole reason this is a function: the
 * last band ends at 100 and its bound is not typable, so appending would make
 * the new band the one that has to end the scale - and the operator would have
 * to retype the 100 they never entered. Inserted before, the new band opens a
 * hole between two known bounds, which is exactly what the editor hatches and
 * what greys the save button until it is filled.
 *
 * The plancher follows its BAND and not its index: everything at or after the
 * insertion point slides by one. The passable band follows its band the same
 * way (#110) - it sits below the plancher, so in practice it never moves.
 */
export function addBand(scoring: CriterionScoringDraft): CriterionScoringDraft {
  const at = scoring.bands.length - 1;
  return {
    ...scoring,
    bands: [...scoring.bands.slice(0, at), { upper: null, text: '' }, ...scoring.bands.slice(at)],
    plancher: scoring.plancher >= at ? scoring.plancher + 1 : scoring.plancher,
    passable:
      scoring.passable !== null && scoring.passable >= at ? scoring.passable + 1 : scoring.passable,
  };
}

/**
 * A band removed, and the two things that have to be put back.
 *
 * ## The plancher follows its band (§6)
 *
 * « Retirer la tranche plancher déplace le plancher sur la suivante ». Three
 * cases, and the third is the one a rule of thumb gets wrong. Removing a band
 * BELOW the plancher slides it down by one - it is the same band, at a new
 * index. Removing one ABOVE leaves it alone. Removing the plancher itself
 * leaves the INDEX alone, which now names the band that followed it - unless it
 * was the last, where the index would fall off the end and the previous band
 * takes the role.
 *
 * ## The scale still ends at 100, and that is not cosmetic
 *
 * The last band's bound is NOT typable (§6: « la dernière figée à 100 »), so
 * the band that becomes last has to be given the 100 the removed one carried -
 * it absorbs the top of the scale, which is what a contiguous list of bands
 * means. Left alone, the row would read « 40 - 100 » over a draft holding 75:
 * `scoringProblems` would answer `last-not-100` forever, the save button would
 * stay grey, and the operator would have no control to repair it - « Ajouter
 * une tranche » inserts BEFORE the last, so even that is no way out.
 */
export function removeBand(scoring: CriterionScoringDraft, index: number): CriterionScoringDraft {
  const wasLast = index === scoring.bands.length - 1;
  const plancher =
    index < scoring.plancher
      ? scoring.plancher - 1
      : index === scoring.plancher && wasLast
        ? Math.max(0, scoring.plancher - 1)
        : scoring.plancher;
  /*
    The passable band follows its band too (#110), with one difference: when
    ITS band goes it is CLEARED rather than moved, because unlike the plancher
    it is optional - moving it would pick a band nobody chose, clearing it
    asks the operator again. It is cleared as well when the plancher comes
    down onto it, which removing the last band can do.
  */
  const passable =
    scoring.passable === null || index === scoring.passable
      ? null
      : index < scoring.passable
        ? scoring.passable - 1
        : scoring.passable;
  const bands = scoring.bands.filter((_band, at) => at !== index);
  return {
    ...scoring,
    bands: bands.map((band, at) =>
      wasLast && at === bands.length - 1 ? { ...band, upper: CRITERION_SCORING_TOP } : band,
    ),
    plancher,
    passable: passable !== null && passable < plancher ? passable : null,
  };
}

/**
 * The plancher moved to another band, from its radio (§6).
 *
 * A passable band left AT or above the new plancher is cleared (#110): it would
 * be refused on save (`passable-not-below-plancher`), and a refusal the gesture
 * itself caused is one the operator did not see coming.
 */
export function withPlancher(
  scoring: CriterionScoringDraft,
  plancher: number,
): CriterionScoringDraft {
  return {
    ...scoring,
    plancher,
    passable: scoring.passable !== null && scoring.passable < plancher ? scoring.passable : null,
  };
}

/** The passable band marked on a band, or cleared with null (#110). */
export function withPassable(
  scoring: CriterionScoringDraft,
  passable: number | null,
): CriterionScoringDraft {
  return { ...scoring, passable };
}

/**
 * The bands a passable band can be marked on (#110): every band STRICTLY below
 * the plancher, each with its lower bound - null while the bound above it has
 * not been typed, which the select says rather than guessing.
 */
export function passableChoices(
  scoring: CriterionScoringDraft,
): { readonly index: number; readonly lower: number | null }[] {
  const lowers = bandLowerBounds(scoring.bands);
  return scoring.bands
    .slice(0, Math.min(scoring.plancher, scoring.bands.length))
    .map((_band, index) => ({ index, lower: lowers[index] ?? null }));
}

/**
 * The number an upper-bound field holds after a keystroke, or the value it had.
 *
 * An empty field is `null` - the band whose bound has not been typed - and
 * anything that is not an integer of the scale is REFUSED rather than clamped:
 * clamping « 0 » to 1 would write a bound nobody chose, and a field that
 * rewrites what is typed under the caret is a field an operator stops trusting.
 * Zero itself IS accepted, because it is what « 05 » looks like halfway
 * through, and `scoringProblems` calls it non-increasing until it grows.
 */
export function bandUpperFromInput(raw: string, previous: number | null): number | null {
  const text = raw.trim();
  if (text === '') return null;
  if (!/^\d{1,3}$/.test(text)) return previous;
  const value = Number.parseInt(text, 10);
  return value <= CRITERION_SCORING_TOP ? value : previous;
}
